import { useEffect, useState } from "react";
import axios from "axios";
import {
  Filter, Users, Video, Eye, UserCheck, Wallet, TrendingDown, RefreshCw,
} from "lucide-react";
import { BACKEND_URL } from "../../lib/apiBase";
import { useNavigate } from "react-router-dom";

const API = `${BACKEND_URL}/api`;
const fmt = (n) => (n || 0).toLocaleString("de-CH");

const STAGE_META = {
  leads: { icon: Users, color: "#8B5CF6" },
  webinar_reg: { icon: Video, color: "#6366F1" },
  webinar_att: { icon: Eye, color: "#3B82F6" },
  customers: { icon: UserCheck, color: "#0EA5E9" },
  deposited: { icon: Wallet, color: "#10B981" },
};

const SOURCE_LABELS = {
  lead_magnet: "Lead-Magnet",
  webinar: "Webinar",
  ai_form: "AI-Formular",
  unbekannt: "Unbekannt",
};

const RANGES = [
  { days: 30, label: "30 Tage" },
  { days: 90, label: "90 Tage" },
  { days: 365, label: "12 Monate" },
  { days: 0, label: "Gesamt" },
];

export default function FunnelView() {
  const [days, setDays] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem("alphalane_admin_token") : null;

  useEffect(() => {
    if (!token) return navigate("/admin/login");
    setLoading(true);
    axios
      .get(`${API}/admin/funnel?days=${days}`, { headers: { "x-admin-token": token } })
      .then((r) => setData(r.data))
      .catch((err) => {
        if (err?.response?.status === 401) {
          localStorage.removeItem("alphalane_admin_token");
          navigate("/admin/login");
        }
      })
      .finally(() => setLoading(false));
  }, [days, token, navigate]);

  const stages = data?.stages || [];
  const maxCount = Math.max(1, ...stages.map((s) => s.count));

  return (
    <div data-testid="funnel-view">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="font-display font-bold text-2xl">Funnel-Analytics</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Drop-offs von Lead → Webinar → Einzahlung. Demo-Daten sind ausgeschlossen.
          </p>
        </div>
        <div className="inline-flex p-1 rounded-full bg-white border border-[var(--al-line)] shadow-sm">
          {RANGES.map((r) => (
            <button
              key={r.days}
              onClick={() => setDays(r.days)}
              data-testid={`funnel-range-${r.days}`}
              className={`px-3.5 py-1.5 rounded-full text-xs font-display font-semibold transition-all ${
                days === r.days ? "al-grad-bg text-white shadow-sm" : "text-slate-500 hover:text-slate-900"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="al-card p-10 flex items-center justify-center text-slate-400 gap-2">
          <RefreshCw className="h-4 w-4 animate-spin" /> Lade Funnel…
        </div>
      ) : (
        <>
          {/* Gesamt-Conversion */}
          <div className="al-card p-5 mb-4 flex flex-wrap items-center gap-4" data-testid="funnel-overall">
            <div className="h-11 w-11 rounded-2xl al-grad-bg text-white flex items-center justify-center">
              <Filter className="h-5 w-5" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">
                Gesamt-Conversion (Lead → Einzahlung)
              </div>
              <div className="font-display font-bold text-2xl">
                {data?.overall_conversion_pct != null ? `${data.overall_conversion_pct} %` : "—"}
              </div>
            </div>
          </div>

          {/* Trichter */}
          <section className="al-card p-5 sm:p-7 mb-4" data-testid="funnel-stages">
            <div className="space-y-3">
              {stages.map((s, i) => {
                const meta = STAGE_META[s.id] || STAGE_META.leads;
                const Icon = meta.icon;
                const width = Math.max(6, (s.count / maxCount) * 100);
                return (
                  <div key={s.id} data-testid={`funnel-stage-${s.id}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4" style={{ color: meta.color }} />
                        <span className="font-display font-semibold text-sm">{s.label}</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        {s.conversion_pct != null && (
                          <span
                            className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                              s.conversion_pct >= 50
                                ? "bg-emerald-100 text-emerald-700"
                                : s.conversion_pct >= 20
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-rose-100 text-rose-700"
                            }`}
                          >
                            {s.conversion_pct} %
                          </span>
                        )}
                        <span className="font-mono-tab font-bold">{fmt(s.count)}</span>
                      </div>
                    </div>
                    <div className="h-9 rounded-xl bg-slate-100 overflow-hidden">
                      <div
                        className="h-full rounded-xl transition-all duration-500 flex items-center px-3"
                        style={{
                          width: `${width}%`,
                          background: `linear-gradient(90deg, ${meta.color}, ${meta.color}99)`,
                        }}
                      />
                    </div>
                    {s.drop_off > 0 && (
                      <div className="mt-1 flex items-center gap-1.5 text-[11px] text-rose-500">
                        <TrendingDown className="h-3 w-3" />
                        {fmt(s.drop_off)} Drop-off gegenüber vorheriger Stufe
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="mt-5 text-[11px] text-slate-400 leading-relaxed">
              Datenquellen: Leads (CRM) · Zoho-Registranten &amp; WhatsApp-Opt-ins (Anmeldungen) ·
              Zoho-Attendance-Sync (Teilnahmen) · Kundenkonten (Registrierung &amp; Einzahlung).
              Stufen sind unabhängige Mengen — z.&nbsp;B. können Kunden auch ohne Webinar einsteigen.
            </p>
          </section>

          {/* Lead-Quellen */}
          <section className="al-card p-5 sm:p-7" data-testid="funnel-sources">
            <div className="font-display font-bold text-lg mb-4">Leads nach Quelle</div>
            {(data?.lead_sources || []).length === 0 ? (
              <div className="text-sm text-slate-500">Keine Leads im gewählten Zeitraum.</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {data.lead_sources.map((s) => (
                  <div
                    key={s.source}
                    className="flex items-center gap-2 px-3.5 py-2 rounded-full border border-slate-200 bg-slate-50/60"
                    data-testid={`funnel-source-${s.source}`}
                  >
                    <span className="font-display font-semibold text-sm">
                      {SOURCE_LABELS[s.source] || s.source}
                    </span>
                    <span className="font-mono-tab text-xs text-slate-500">{fmt(s.count)}</span>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
